import { Card } from "primereact/card";
import { Column } from "primereact/column";
import { DataTable, DataTablePFSEvent } from "primereact/datatable";
import { Message } from "primereact/message";
import { Tag } from "primereact/tag";
import { useState } from "react";
import { useGetSftpPartnerJobsQuery } from "store/queries/sftpPartners";
import { ISftpPartnerJob, SftpProcessingJobStatus } from "types/SftpPartner";

import { formatDateTime } from "../../utils/hstImports";
import { humanizeEnum } from "../../utils/sftpPartners";

const PAGE_SIZE = 25;

const STATUS_SEVERITY: Record<
  SftpProcessingJobStatus,
  "success" | "info" | "warning" | "danger"
> = {
  queued: "warning",
  running: "info",
  completed: "success",
  failed: "danger",
  dead: "danger",
};

const statusBody = (j: ISftpPartnerJob) => (
  <Tag
    value={humanizeEnum(j.status)}
    severity={STATUS_SEVERITY[j.status] ?? "info"}
  />
);

const typeBody = (j: ISftpPartnerJob) => humanizeEnum(j.jobType);

const attemptsBody = (j: ISftpPartnerJob) => `${j.attempts} / ${j.maxAttempts}`;

const lastErrorBody = (j: ISftpPartnerJob) =>
  j.lastError ? (
    <span className="text-red-600 text-sm" style={{ wordBreak: "break-word" }}>
      {j.lastError}
    </span>
  ) : (
    "—"
  );

const dateBody = (key: "enqueuedAt" | "startedAt" | "finishedAt") =>
  function DateCell(j: ISftpPartnerJob) {
    return j[key] ? formatDateTime(j[key]) : "—";
  };

/**
 * CLE-2950 — the partner's EDI processing jobs (EdiProcessingJob), newest enqueued first, paged on the
 * server. Read-only: the reaper re-queues expired leases; there is no retry from here.
 */
export function SftpPartnerJobsCard({ slug }: { slug: string }) {
  const [page, setPage] = useState(0);
  const { data, isFetching, isError } = useGetSftpPartnerJobsQuery({
    slug,
    page,
    pageSize: PAGE_SIZE,
  });
  const result = data?.data;

  return (
    <Card title="Processing jobs" className="mb-3">
      {isError ? (
        <Message
          severity="error"
          text="Couldn't load this partner's processing jobs."
        />
      ) : (
        <DataTable
          value={result?.jobs ?? []}
          dataKey="id"
          lazy
          paginator={(result?.total ?? 0) > PAGE_SIZE}
          first={page * PAGE_SIZE}
          rows={PAGE_SIZE}
          totalRecords={result?.total ?? 0}
          onPage={(e: DataTablePFSEvent) =>
            setPage(Math.floor(e.first / PAGE_SIZE))
          }
          loading={isFetching}
          emptyMessage="No processing jobs."
          responsiveLayout="scroll"
          data-testid="sftp-partner-jobs"
        >
          <Column header="Status" body={statusBody} />
          <Column header="Type" body={typeBody} />
          <Column header="Attempts" body={attemptsBody} />
          <Column header="Last error" body={lastErrorBody} />
          <Column header="Enqueued" body={dateBody("enqueuedAt")} />
          <Column header="Started" body={dateBody("startedAt")} />
          <Column header="Finished" body={dateBody("finishedAt")} />
        </DataTable>
      )}
    </Card>
  );
}
